import { createContext, useContext, useId, useLayoutEffect, useSyncExternalStore, type ReactNode } from "react";

type Back = { id: string; label: ReactNode; onBack: () => void };
type Snapshot = { backs: Back[]; headers: string[] };
const empty: Snapshot = { backs: [], headers: [] };
export function createNavigationStore() {
  let snapshot = empty;
  const listeners = new Set<() => void>();
  const publish = (next: Snapshot) => { snapshot = next; listeners.forEach(listener => listener()); };
  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    registerBack: (id: string) => {
      publish({ ...snapshot, backs: [...snapshot.backs, { id, label: null, onBack: () => {} }] });
      return () => publish({ ...snapshot, backs: snapshot.backs.filter(item => item.id !== id) });
    },
    updateBack: (back: Back) => {
      const previous = snapshot.backs.find(item => item.id === back.id);
      if (!previous || (previous.label === back.label && previous.onBack === back.onBack)) return;
      publish({ ...snapshot, backs: snapshot.backs.map(item => item.id === back.id ? back : item) });
    },
    registerHeader: (id: string) => {
      publish({ ...snapshot, headers: [...snapshot.headers, id] });
      return () => publish({ ...snapshot, headers: snapshot.headers.filter(item => item !== id) });
    },
  };
}
export const PageHeaderNavigationContext = createContext<ReturnType<typeof createNavigationStore> | null>(null);
const subscribeEmpty = () => () => {};
const getEmpty = () => empty;

export function usePageBackSource(label: ReactNode, onBack: () => void) {
  const store = useContext(PageHeaderNavigationContext);
  const id = useId();
  const snapshot = useSyncExternalStore(store?.subscribe ?? subscribeEmpty, store?.getSnapshot ?? getEmpty, getEmpty);
  useLayoutEffect(() => store?.registerBack(id), [store, id]);
  useLayoutEffect(() => { store?.updateBack({ id, label, onBack }); }, [store, id, label, onBack]);
  return snapshot.backs[0]?.id === id && snapshot.headers.length > 0;
}

export function usePageHeaderBack() {
  const store = useContext(PageHeaderNavigationContext);
  const id = useId();
  const snapshot = useSyncExternalStore(store?.subscribe ?? subscribeEmpty, store?.getSnapshot ?? getEmpty, getEmpty);
  useLayoutEffect(() => store?.registerHeader(id), [store, id]);
  return snapshot.headers[0] === id ? snapshot.backs[0] : undefined;
}
